import { useId, useState } from "react";

import { useTranslation } from "@/core/i18n";
import { Checkbox } from "atoms/checkbox/Checkbox";
import { Radio } from "atoms/radio/Radio";
import { TextButton } from "atoms/text-button/TextButton";

import type { ListingSortOptions } from "./listingFilterOptions";

type Option = {
  id: string;
  label: string;
  /** How many listings survive this option; `undefined` means "not counted". */
  count?: number;
};

type FilterGroup = {
  title: string;
  options: Option[];
  selectedIds: string[];
  onToggle: (id: string) => void;
};

interface Props {
  sort: {
    title: string;
    options: Option[];
    selectedId: string;
    onSelect: (id: ListingSortOptions) => void;
  };
  groups: FilterGroup[];
  /** Rendered only when at least one filter is active. */
  onReset?: () => void;
}

/** A group longer than this is collapsed; revealing only a row or two is not worth a click. */
const COLLAPSE_ABOVE_OPTION_COUNT = 8;
/** The options a collapsed group keeps on screen, besides any the patient has selected. */
const COLLAPSED_OPTION_COUNT = 6;

/**
 * The options worth offering: one that leaves no listings is hidden (the phone modals only offer
 * options with results too), except while it is selected, so it can still be cleared.
 */
const offeredOptions = ({ options, selectedIds }: FilterGroup) =>
  options.filter((option) => option.count !== 0 || selectedIds.includes(option.id));

const FilterGroupSection = ({ group, options }: { group: FilterGroup; options: Option[] }) => {
  const { t } = useTranslation();
  const listId = useId();
  const [isExpanded, setIsExpanded] = useState(false);
  const collapsedOptions =
    options.length > COLLAPSE_ABOVE_OPTION_COUNT
      ? options.filter(
          (option, index) =>
            index < COLLAPSED_OPTION_COUNT || group.selectedIds.includes(option.id),
        )
      : options;
  const hiddenCount = options.length - collapsedOptions.length;

  return (
    <section className="flex flex-col gap-1">
      <h2 className="pb-1 text-sub2 text-secondary">{group.title}</h2>
      <div id={listId} className="flex flex-col gap-1">
        {(isExpanded ? options : collapsedOptions).map((option) => (
          <Checkbox
            key={option.id}
            label={option.count ? `${option.label} (${option.count})` : option.label}
            checked={group.selectedIds.includes(option.id)}
            // `!` because the atom's own `m-2.5` sorts later in the stylesheet and would win,
            // leaving 44px rows beside the 32px sort radios.
            inputClassName="!my-1"
            labelClassName="text-body2"
            onChange={() => group.onToggle(option.id)}
          />
        ))}
      </div>
      {hiddenCount > 0 && (
        <div className="flex">
          <TextButton
            aria-expanded={isExpanded}
            aria-controls={listId}
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <span className="text-bold2 text-brand-color">
              {isExpanded ? t("common.showLess") : `${t("common.showMore")} (${hiddenCount})`}
            </span>
          </TextButton>
        </div>
      )}
    </section>
  );
};

/**
 * Persistent facet rail for the shop lists, from `lg` up.
 *
 * On a phone these choices live behind bottom-sheet modals because there is no
 * room for them; on a desktop that indirection is pure cost — the whole point
 * of the width is that the patient can see what is available, narrow it, and
 * watch the results change without ever opening a dialog. Below `lg` the
 * modal buttons stay, so the phone flow is unchanged.
 */
export const ShopFilterRail = ({ sort, groups, onReset }: Props) => {
  const { t } = useTranslation();
  const visibleGroups = groups
    .map((group) => ({ group, options: offeredOptions(group) }))
    .filter(({ options }) => options.length > 0);
  const hasActiveFilters = visibleGroups.some(({ group }) => group.selectedIds.length > 0);

  return (
    <div className="sticky top-24 flex max-h-[calc(100vh-8rem)] flex-col gap-7 overflow-y-auto pb-8 pr-2">
      <section className="flex flex-col gap-1">
        <h2 className="pb-1 text-sub2 text-secondary">{sort.title}</h2>
        {sort.options.map((option) => (
          <Radio
            key={option.id}
            name="shop-sort"
            height="h-8"
            label={option.label}
            checked={sort.selectedId === option.id}
            labelClassName="text-body2"
            onChange={() => sort.onSelect(option.id as ListingSortOptions)}
          />
        ))}
      </section>

      {visibleGroups.map(({ group, options }) => (
        <FilterGroupSection key={group.title} group={group} options={options} />
      ))}

      {hasActiveFilters && onReset && (
        <div className="flex">
          <TextButton onClick={onReset}>
            <span className="text-bold2 text-brand-color">
              {t("filterAndSortListings.resetFilter")}
            </span>
          </TextButton>
        </div>
      )}
    </div>
  );
};
